import { AudienceModeId, normalizeAudienceMode } from "./audienceModes";

export type ViewDetailsSectionId =
  | 'overview'
  | 'employment'
  | 'opportunity_sales'
  | 'contacts'
  | 'website_audit'
  | 'ai_consultant'
  | 'scores'
  | 'health_history'
  | 'research';

export interface AudienceDetailConfig {
  modeId: AudienceModeId;
  sectionTitles: {
    overview: string;
    employment: string;
    opportunity_sales: string;
    contacts: string;
    website_audit: string;
    ai_consultant: string;
    scores: string;
    health_history: string;
    research: string;
  };
  sectionSubtitles: {
    overview: string;
    employment?: string;
    opportunity_sales?: string;
    contacts: string;
    website_audit?: string;
    ai_consultant?: string;
    scores: string;
    health_history: string;
    research: string;
  };
  visibleSections: ViewDetailsSectionId[];
  primaryActions: {
    saveLabel: string;
    contactLabel: string;
    secondaryLabel?: string;
  };
  audienceBadges: {
    primaryTag: string;
    secondaryTag: string;
  };
  customExplanations: {
    whyThisAppears: string;
    opportunitySummaryHeader: string;
  };
}

export const AUDIENCE_DETAIL_CONFIGS: Record<AudienceModeId, AudienceDetailConfig> = {
  job_seeker: {
    modeId: "job_seeker",
    sectionTitles: {
      overview: "Employer Opportunity Summary",
      employment: "1. Salary, Benefits & Workplace Quality",
      opportunity_sales: "2. Workplace Readiness & Growth",
      contacts: "3. Direct Employer Contacts & Applications",
      website_audit: "4. Business & Career Portal Audit",
      ai_consultant: "5. Career Advisor & Application Assistant",
      scores: "6. Job Search Compatibility Factors",
      health_history: "7. Employer Stability & Historical Activity",
      research: "8. Quick Employer Research Links"
    },
    sectionSubtitles: {
      overview: "Estimated compensation, commute time, and active hiring signals for job seekers.",
      employment: "Transparent pay ranges, healthcare, PTO, and workplace flexibility indicators.",
      opportunity_sales: "Operational footprint, team growth trajectory, and hiring demand.",
      contacts: "Verified owner, hiring manager, and application contact information.",
      website_audit: "Official business website and career page availability review.",
      ai_consultant: "Generate tailored application letters and outreach suggestions.",
      scores: "Breakdown of commute, hiring signals, and location factors.",
      health_history: "Employer rating trends and saved activity timeline.",
      research: "Verified search links on LinkedIn, Facebook, Yelp, and official records."
    },
    visibleSections: ['overview', 'employment', 'contacts', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save Target Employer",
      contactLabel: "Contact Employer",
      secondaryLabel: "Plan Route / Commute"
    },
    audienceBadges: {
      primaryTag: "Possible Employer",
      secondaryTag: "Active Hiring Match"
    },
    customExplanations: {
      whyThisAppears: "This business appears because it shows active hiring indicators or local operational growth within convenient commuting distance.",
      opportunitySummaryHeader: "Job Seeker Opportunity Match"
    }
  },

  business_explorer: {
    modeId: "business_explorer",
    sectionTitles: {
      overview: "Business Overview",
      employment: "1. Ratings & Customer Reputation",
      opportunity_sales: "2. Popularity & Brand Recognition",
      contacts: "3. Contact Channels & Location",
      website_audit: "4. Services, Features & Amenities",
      ai_consultant: "5. Local Explorer Assistant",
      scores: "6. Business Explorer Match Breakdown",
      health_history: "7. Saved Activity & Visits History",
      research: "8. Online Links & Reviews"
    },
    sectionSubtitles: {
      overview: "Business category, address, distance, open status, and popularity level.",
      employment: "Customer review volume, star ratings, and local reputation.",
      opportunity_sales: "Popularity level, brand recognition, and customer awareness signals.",
      contacts: "Verified address, phone number, website link, and directions.",
      website_audit: "Services offered, booking options, accessibility, and price level.",
      ai_consultant: "Get recommendations, visit tips, and custom local itineraries.",
      scores: "Breakdown of category relevance, distance, ratings, and popularity score.",
      health_history: "Saved place notes, visit status, and personal logs.",
      research: "Direct links to website, directions, menu/booking, and reviews."
    },
    visibleSections: ['overview', 'opportunity_sales', 'employment', 'contacts', 'website_audit', 'scores', 'research'],
    primaryActions: {
      saveLabel: "Save Business",
      contactLabel: "Call Business",
      secondaryLabel: "Get Directions"
    },
    audienceBadges: {
      primaryTag: "Local Business",
      secondaryTag: "Popularity Level"
    },
    customExplanations: {
      whyThisAppears: "This business appears based on category match, proximity, open status, customer ratings, and brand popularity near your search location.",
      opportunitySummaryHeader: "Business Explorer Match"
    }
  },

  recruiter: {
    modeId: "recruiter",
    sectionTitles: {
      overview: "Recruiting Prospect Account Overview",
      employment: "1. Hiring Demand & Staffing Telemetry",
      opportunity_sales: "2. Account Scale & Facility Expansion",
      contacts: "3. Decision Makers & Hiring Managers",
      website_audit: "4. Corporate Web & Career Portal Inspection",
      ai_consultant: "5. Recruiter Pitch & Account Intelligence",
      scores: "6. Recruiting Priority Score Breakdown",
      health_history: "7. Account Growth & Historical Activity",
      research: "8. Executive & Company Research"
    },
    sectionSubtitles: {
      overview: "Hiring volume, open vacancy trends, and decision-maker contact paths for talent acquisition.",
      employment: "Hiring urgency index, job posting volume, and headcount expansion indicators.",
      opportunity_sales: "Multi-branch footprint and operational capacity.",
      contacts: "Direct owner, HR Director, and talent acquisition lead contacts.",
      website_audit: "Careers page responsiveness, applicant tracking systems, and portal health.",
      ai_consultant: "Draft cold email pitches and account intake call scripts.",
      scores: "Hiring demand, recency, and contact availability factor breakdown.",
      health_history: "Account recruitment history and interaction logs.",
      research: "Executive profiles on LinkedIn, company filings, and news."
    },
    visibleSections: ['overview', 'employment', 'contacts', 'ai_consultant', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save Recruiting Prospect",
      contactLabel: "Contact Hiring Manager",
      secondaryLabel: "Draft Fee Agreement Pitch"
    },
    audienceBadges: {
      primaryTag: "Hiring Employer Target",
      secondaryTag: "Recruiting Prospect"
    },
    customExplanations: {
      whyThisAppears: "This business appears because it exhibits active recruitment activity, open headcount demand, or expanding local operations.",
      opportunitySummaryHeader: "Recruiter Account Priority"
    }
  },

  staffing_agency: {
    modeId: "staffing_agency",
    sectionTitles: {
      overview: "Staffing Prospect Summary",
      employment: "1. Labor Demand & Shift Operations",
      opportunity_sales: "2. Operational Capacity & Field Logistics",
      contacts: "3. Staffing Decision Makers & General Managers",
      website_audit: "4. Web Portal & Portal Accessibility",
      ai_consultant: "5. Staffing Proposal & Outreach Workspace",
      scores: "6. Staffing Need Indicators Breakdown",
      health_history: "7. Staffing Demand History & Activity",
      research: "8. Industry & Registry Lookup"
    },
    sectionSubtitles: {
      overview: "High-volume hiring, shift labor demand, and seasonal labor requirements.",
      employment: "Turnover-intensive industry profile, shift coverage needs, and seasonal spikes.",
      opportunity_sales: "Multi-site operations and field crew requirements.",
      contacts: "General Managers, Operations Directors, and HR decision makers.",
      website_audit: "Online contact forms and direct communication channels.",
      ai_consultant: "Structure custom temp-to-hire and shift staffing proposals.",
      scores: "Labor volume, turnover profile, and phone availability breakdown.",
      health_history: "Past staffing interactions and operational activity.",
      research: "Industry registry and commercial footprint research."
    },
    visibleSections: ['overview', 'employment', 'contacts', 'ai_consultant', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save Staffing Lead",
      contactLabel: "Call Decision Maker",
      secondaryLabel: "Send Staffing Proposal"
    },
    audienceBadges: {
      primaryTag: "High-Volume Employer",
      secondaryTag: "Shift Labor Demand"
    },
    customExplanations: {
      whyThisAppears: "This business appears because its operational profile indicates high turnover, seasonal demand, or recurring temporary staffing requirements.",
      opportunitySummaryHeader: "Staffing Lead Opportunity Fit"
    }
  },

  career_coach: {
    modeId: "career_coach",
    sectionTitles: {
      overview: "Client Opportunity Match Overview",
      employment: "1. Compensation, Benefits & Culture Audit",
      opportunity_sales: "2. Workplace Quality & Growth Trajectory",
      contacts: "3. Employer Contacts & Networking Paths",
      website_audit: "4. Employer Web & Brand Review",
      ai_consultant: "5. Client Positioning & Outreach Coaching",
      scores: "6. Client Match Factor Breakdown",
      health_history: "7. Client Opportunity History & Tracking",
      research: "8. Employer Research & Verification"
    },
    sectionSubtitles: {
      overview: "Workplace rating, compensation transparency, and career growth potential for coaching clients.",
      employment: "Pay structure, mentorship, work-life balance, and employee ratings.",
      opportunity_sales: "Stability, review sentiment, and career advancement paths.",
      contacts: "Direct hiring contacts and strategic networking pathways.",
      website_audit: "Digital reputation, mission clarity, and brand presence.",
      ai_consultant: "Prepare client interview prep and cold email templates.",
      scores: "Reputation rating, hiring alignment, and proximity factors.",
      health_history: "Tracking notes and client assignment activity timeline.",
      research: "Verify company ratings, social pages, and corporate history."
    },
    visibleSections: ['overview', 'employment', 'contacts', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save for Active Client",
      contactLabel: "Contact Hiring Lead",
      secondaryLabel: "Export Client Brief"
    },
    audienceBadges: {
      primaryTag: "Recommended Employer",
      secondaryTag: "Coaching Target"
    },
    customExplanations: {
      whyThisAppears: "This business appears because it matches client workplace criteria, offers competitive compensation signals, and maintains high local review ratings.",
      opportunitySummaryHeader: "Career Coach Client Fit"
    }
  },

  workforce_organization: {
    modeId: "workforce_organization",
    sectionTitles: {
      overview: "Regional Employer Overview",
      employment: "1. Regional Hiring & Apprentice Demand",
      opportunity_sales: "2. Community Impact & Business Footprint",
      contacts: "3. Employer Engagement & Community Contacts",
      website_audit: "4. Public Presence & Contact Portal",
      ai_consultant: "5. Workforce Engagement Strategy",
      scores: "6. Regional Impact Score Breakdown",
      health_history: "7. Community Engagement History",
      research: "8. Business & Municipal Records"
    },
    sectionSubtitles: {
      overview: "Community employer activity, program participant role suitability, and regional engagement pathways.",
      employment: "Apprenticeship, entry-level, and trade role suitability indicators.",
      opportunity_sales: "Community footprint and multi-unit regional operations.",
      contacts: "Business owners, community liaisons, and hiring managers.",
      website_audit: "Public contact channels and online accessibility.",
      ai_consultant: "Generate workforce board outreach scripts and partnership letters.",
      scores: "Regional hiring impact, community track record, and contact score.",
      health_history: "Past referrals and engagement tracking timeline.",
      research: "Verify local registration, municipal standing, and social links."
    },
    visibleSections: ['overview', 'employment', 'contacts', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save Regional Partner",
      contactLabel: "Engage Employer",
      secondaryLabel: "Refer Program Participants"
    },
    audienceBadges: {
      primaryTag: "Community Employer",
      secondaryTag: "Workforce Target"
    },
    customExplanations: {
      whyThisAppears: "This business appears because it represents a key regional employer with potential openings for workforce development participants.",
      opportunitySummaryHeader: "Regional Employer Opportunity"
    }
  },

  college_career_center: {
    modeId: "college_career_center",
    sectionTitles: {
      overview: "Student Opportunity Overview",
      employment: "1. Entry-Level Roles & Internship Quality",
      opportunity_sales: "2. Workplace Readiness & Graduate Fit",
      contacts: "3. Campus Recruitment & HR Contacts",
      website_audit: "4. Career Page & Student Application Portal",
      ai_consultant: "5. Campus Outreach & Partnership Scripting",
      scores: "6. Campus Match Score Breakdown",
      health_history: "7. Student Placement History",
      research: "8. Employer Verification & Directory"
    },
    sectionSubtitles: {
      overview: "Campus proximity, entry-level internships, and student-friendly work schedules.",
      employment: "Internship availability, new grad training, and entry-level role indicators.",
      opportunity_sales: "Company culture, team structure, and student career progression.",
      contacts: "Recruiting leads, HR contacts, and internship coordinators.",
      website_audit: "Careers page accessibility and online application portal quality.",
      ai_consultant: "Draft invitations for career fairs and student internship partnerships.",
      scores: "Commute distance, internship tags, and recruitment readiness breakdown.",
      health_history: "Campus partnership interaction logs and student placement history.",
      research: "Verify local business credentials and employer social profiles."
    },
    visibleSections: ['overview', 'employment', 'contacts', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save Employer Partner",
      contactLabel: "Invite to Career Fair",
      secondaryLabel: "Export Student Guide"
    },
    audienceBadges: {
      primaryTag: "Entry-Level Employer",
      secondaryTag: "Student Placement"
    },
    customExplanations: {
      whyThisAppears: "This business appears because it is located near campus and offers entry-level roles or internship opportunities suitable for students.",
      opportunitySummaryHeader: "Student Opportunity Match"
    }
  },

  sales_professional: {
    modeId: "sales_professional",
    sectionTitles: {
      overview: "B2B Sales Prospect Overview",
      employment: "1. Business Operations & Growth Indicators",
      opportunity_sales: "2. B2B Service & Product Fit Assessment",
      contacts: "3. Key Decision Makers & Contact Finder",
      website_audit: "4. Digital Footprint & Tech Stack Review",
      ai_consultant: "5. AI Sales Pitch & Discovery Generator",
      scores: "6. Sales Priority Factors Breakdown",
      health_history: "7. Prospect Pipeline Activity Timeline",
      research: "8. B2B Corporate Research & Lookup"
    },
    sectionSubtitles: {
      overview: "Contract value estimate, digital infrastructure gaps, and key decision-maker contact paths.",
      employment: "Headcount activity and local operational scale.",
      opportunity_sales: "Estimated deal value, service fit index, and sales opportunity signals.",
      contacts: "Direct owner, GM, and department head contact details.",
      website_audit: "Website status, mobile responsiveness, SSL security, and tech stack audit.",
      ai_consultant: "Generate tailored cold call scripts, emails, and value proposition briefs.",
      scores: "Website gap score, contact availability, and market demand breakdown.",
      health_history: "Pipeline stage updates, notes, and outreach timeline.",
      research: "Look up business registration, LinkedIn leads, and competitor context."
    },
    visibleSections: ['overview', 'opportunity_sales', 'contacts', 'website_audit', 'ai_consultant', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save Sales Prospect",
      contactLabel: "Call Decision Maker",
      secondaryLabel: "Send Pitch Email"
    },
    audienceBadges: {
      primaryTag: "B2B Sales Target",
      secondaryTag: "Service Fit Prospect"
    },
    customExplanations: {
      whyThisAppears: "This business appears because it demonstrates service gaps, web infrastructure needs, or operational growth ideal for B2B solutions.",
      opportunitySummaryHeader: "B2B Sales Opportunity Score"
    }
  },

  business_development: {
    modeId: "business_development",
    sectionTitles: {
      overview: "Strategic Partner Overview",
      employment: "1. Business Scale & Footprint Telemetry",
      opportunity_sales: "2. Partnership Value & Co-Marketing Potential",
      contacts: "3. Executive Leadership & Managing Partners",
      website_audit: "4. Digital Brand Presence Audit",
      ai_consultant: "5. Joint Venture & Partnership Strategy",
      scores: "6. Partnership Fit Breakdown",
      health_history: "7. BD Activity & Relationship Timeline",
      research: "8. Executive Research & Corporate Filings"
    },
    sectionSubtitles: {
      overview: "Multi-location expansion, brand reputation, and executive outreach paths for business development.",
      employment: "Regional expansion signals and multi-unit operating capacity.",
      opportunity_sales: "Strategic alignment, co-branding potential, and referral synergies.",
      contacts: "Chief Executive, Managing Partner, and BD Director contact details.",
      website_audit: "Brand positioning, web authority, and digital presence review.",
      ai_consultant: "Generate joint venture pitch briefs and executive meeting agendas.",
      scores: "Expansion footprint, reputation score, and executive contactability.",
      health_history: "MOU stage updates, partnership notes, and meeting history.",
      research: "Corporate filings, executive LinkedIn profiles, and market news."
    },
    visibleSections: ['overview', 'contacts', 'ai_consultant', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save Partnership Target",
      contactLabel: "Initiate BD Outreach",
      secondaryLabel: "Log Strategic Notes"
    },
    audienceBadges: {
      primaryTag: "Strategic Partner",
      secondaryTag: "Expansion Target"
    },
    customExplanations: {
      whyThisAppears: "This business appears because its multi-location footprint and market standing make it an ideal strategic partner or co-marketing candidate.",
      opportunitySummaryHeader: "Partnership Fit Score"
    }
  },

  agency_freelancer: {
    modeId: "agency_freelancer",
    sectionTitles: {
      overview: "Agency Client Prospect Overview",
      employment: "1. Business Operations & Growth Scale",
      opportunity_sales: "2. Digital Service Opportunities & Deal Value",
      contacts: "3. Business Owner Contact Finder",
      website_audit: "4. Website Review & SEO Gap Audit",
      ai_consultant: "5. Proposal & Audit Pitch Workspace",
      scores: "6. Agency Opportunity Score Breakdown",
      health_history: "7. Prospect Audit & Pipeline History",
      research: "8. Domain & Local Search Lookup"
    },
    sectionSubtitles: {
      overview: "Missing website leads, SSL/mobile responsiveness gaps, and pre-drafted pitch strategies.",
      employment: "Local reputation and operational scale.",
      opportunity_sales: "Estimated project value, retainer potential, and web design gap index.",
      contacts: "Direct phone, owner email, and contact form availability.",
      website_audit: "Mobile test, SSL certificate status, meta tags, and booking integration audit.",
      ai_consultant: "Structure web design proposals, SEO audit summaries, and pitch decks.",
      scores: "Missing website weight, mobile/SSL gap, and rating sweet-spot breakdown.",
      health_history: "Audit history, pitch sent dates, and client response log.",
      research: "Whois, domain lookup, Google Business listing, and Yelp reviews."
    },
    visibleSections: ['overview', 'website_audit', 'opportunity_sales', 'contacts', 'ai_consultant', 'scores', 'health_history', 'research'],
    primaryActions: {
      saveLabel: "Save Agency Prospect",
      contactLabel: "Run Full Audit",
      secondaryLabel: "Send Pitch Proposal"
    },
    audienceBadges: {
      primaryTag: "Agency Prospect",
      secondaryTag: "Web/SEO Gap Lead"
    },
    customExplanations: {
      whyThisAppears: "This business appears because it lacks a modern mobile website, has security/SEO gaps, or presents a high-value retainer opportunity.",
      opportunitySummaryHeader: "Agency Service Prospect Score"
    }
  }
};

export function getAudienceDetailConfig(modeId?: string | null): AudienceDetailConfig {
  const normalized = normalizeAudienceMode(modeId);
  return AUDIENCE_DETAIL_CONFIGS[normalized] || AUDIENCE_DETAIL_CONFIGS.job_seeker;
}
